<script lang="ts">
  // d3 imports
  import { timeFormat } from 'd3-time-format';
  import { scaleLinear, scaleBand } from 'd3-scale';
  import { extent } from 'd3-array';

  // Utils
  import { monthFormatter } from '$utils/formatter';

  // Types
  import type { BarChartProps } from './types';
  import { getBarX, getXTicks } from './utils';

  let {
    height = 400,
    zeroBased = true,
    unit = '%',
    dateFormatter,
    data,
    xValKey,
    yValKey,
    stackedYValKey,
    customYDomain,
    customXDomain,
    id,
    class: cls = 'bar-chart',
    xTicks,
    yTicks = 5,
    customXTicks,
    barColour = 'steelblue',
    stackedBarColour = 'navy',
    barWidthRatio = 0.8,
    yTickFormatter,
    annotations,
    endValMargin = 0,
  }: BarChartProps<Record<string, unknown>> = $props();

  // Set d3 date parser and formatter
  let dateFormatterFn = timeFormat(dateFormatter);

  // Set the y and x axis domains and scales
  const yDomain = customYDomain || extent(data.map((x) => +x[yValKey]));

  const yScale =
    zeroBased ?
      scaleLinear().domain([0, yDomain[1]]).range([100, 0])
    : scaleLinear().domain(yDomain).range([100, 0]);

  // X-Axis setup.  Using scaleBand for categorical data.  If you want to adapt for time, you'll need to change this.
  const xDomain = customXDomain || data.map((x) => x[xValKey]);
  const xScale = scaleBand().domain(xDomain).range([0, 100]).padding(0.2); // space between bars

  const barWidth = xScale.bandwidth() * barWidthRatio;
  const innerPadding = xScale.paddingInner();

  // Set the ticks
  let xTicksArr = getXTicks(xDomain, xTicks, customXTicks);

  const yTicksArr = yScale.ticks(yTicks);
</script>

<div class="bar-chart-container {cls}" {id} style="height: {height}px">
  <!-- base layer -->
  <svg
    class="layer"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    style="margin-right:{endValMargin}px;width: calc(100% - {endValMargin}px);"
  >
    <!-- Stripe pattern -->
    <!-- <defs>
      <pattern
        id="pattern-stripe"
        width="1"
        height="2"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width=".5" height="2" transform="translate(0,0)" fill="white"
        ></rect>
      </pattern>
      <mask id="mask-stripe">
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="url(#pattern-stripe)"
        />
      </mask>
    </defs> -->

    {#each xTicksArr as tick}
      <line
        class="tick x"
        y1="100"
        y2="104"
        x1={xScale(tick) + xScale.bandwidth() / 2}
        x2={xScale(tick) + xScale.bandwidth() / 2}
      ></line>
    {/each}

    <!-- Non-0 y axis -->
    {#each yTicksArr as tick}
      {#if tick !== 0}
        <line
          class="tick y"
          class:zero={tick == 0}
          x1="-50"
          x2="100"
          y1={yScale(tick)}
          y2={yScale(tick)}
        ></line>
      {/if}
    {/each}

    <!-- the actual data part -->
    {#each data as d}
      <!-- Stacked bars that go on top if `stackedYValKey` exists -->
      {#if stackedYValKey && +d[xValKey] >= 2025}
        <rect
          class="stacked-data-bar {yValKey} year-{d[xValKey]}"
          x={getBarX(d, xScale, barWidth, xValKey)}
          y={+d[stackedYValKey] < 0 ? yScale(0) : yScale(d[stackedYValKey])}
          width={barWidth}
          height={+d[stackedYValKey] < 0 ?
            yScale(d[stackedYValKey]) - yScale(0)
          : yScale(0) - yScale(d[stackedYValKey])}
          style="fill:{stackedBarColour}"
        />
      {/if}

      <!-- Bars on the bottom -->
      <rect
        class="data-bar {yValKey} year-{d[xValKey]}"
        x={getBarX(d, xScale, barWidth, xValKey)}
        y={+d[yValKey] < 0 ? yScale(0) : yScale(d[yValKey])}
        width={barWidth}
        height={+d[yValKey] < 0 ?
          yScale(d[yValKey]) - yScale(0)
        : yScale(0) - yScale(d[yValKey])}
        style="fill:{barColour}"
      />
    {/each}

    <!-- 0 y-axis -->
    {#each yTicksArr as tick}
      {#if tick === 0}
        <line
          class="tick y"
          class:zero={tick == 0}
          x1="-50"
          x2="100"
          y1={yScale(tick)}
          y2={yScale(tick)}
        ></line>
      {/if}
    {/each}
  </svg>

  <!-- top layer -->
  <div
    class="layer"
    style="margin-right:{endValMargin}px;width: calc(100% - {endValMargin}px);"
  >
    <!-- Draw a rectangle between x=2025 and to the right edge -->
    <div
      class="background bar-chart-overlay"
      style="top:{yScale(6.5)}%; left:{xScale('2025') -
        innerPadding}%; height:{yScale(-3.8)}%; width:{(barWidth +
        innerPadding) *
        3 +
        innerPadding}%; "
    ></div>

    <!-- <div
    class="annotation {anno.cls}"
    style="top:{yScale(anno.yPos)}%; left:calc({xScale(
      anno.xPos
    )}% + {anno.xPosMargin || 0}px); width:{anno.width}px"
  > -->

    {#each yTicksArr as tick, i}
      <p
        class="tick-label y on-chart-text font-sans text-secondary"
        style="top: {yScale(tick)}%; left:0%"
      >
        {yTickFormatter ? yTickFormatter(tick) : tick}{(
          i == yTicksArr.length - 1
        ) ?
          unit
        : ''}
      </p>
    {/each}

    {#each xTicksArr as tick}
      <p
        class="tick-label x on-chart-text font-sans text-secondary"
        style="left: calc({xScale(tick) +
          xScale.bandwidth() / 2}% - 16px); top:calc(100% + 15px)"
      >
        {typeof tick === 'string' ?
          `’${monthFormatter(tick).slice(-2)}`
        : `’${monthFormatter(dateFormatterFn(tick)).slice(-2)}`}
      </p>
    {/each}

    {#if annotations}
      {#each annotations as anno}
        {#if anno.withLeaderLine}
          <div
            class="annotation anno-line-container {anno.cls}"
            style="top:{yScale(anno.yPos)}%; left:calc({xScale(
              anno.xPos
            )}% + {anno.xPosMargin || 0}px); width:{anno.width}px"
          >
            <div class="annotation-line"></div>
            <div class="annotation-text">
              {anno.text}
            </div>
          </div>
        {:else}
          <div
            class="annotation {anno.cls}"
            style="top:{yScale(anno.yPos)}%; left:calc({xScale(
              anno.xPos
            )}% + {anno.xPosMargin || 0}px); width:{anno.width}px"
          >
            {anno.text}
          </div>
        {/if}
      {/each}
    {/if}
  </div>
</div>

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;

  .bar-chart-container {
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    margin-left: 30px;
    margin-bottom: 40px;
    margin-top: 5px;
    margin-right: 48px;
    width: calc(100% - 78px);
    height: calc(100% - 45px);
    overflow: visible;
  }

  line.tick {
    fill: none;
    stroke: var(--tr-muted-grey);
    vector-effect: non-scaling-stroke;
  }

  line.tick.y.zero {
    stroke: var(--tr-dark-grey);
  }

  .annotation,
  p.on-chart-text {
    position: absolute;
  }

  .annotation-text {
    width: 60px;
    line-height: 1.2;
    transform: translate(2px, 9px);
  }

  .annotation {
    color: mixins.$theme-colour-text-secondary;
    font-size: var(--theme-font-size-sm) !important;
  }

  .anno-line-container {
    display: flex; /* Use flexbox to easily align the line and text */
    align-items: center; /* Vertically center the line and text */
    /* Add any other styling for the container here */

    .annotation-line {
      height: 1.6px;
      width: 20px;
      background-color: #666; //var(--tr-dark-grey);
      margin-right: 2px; /* Add a small space between the line and the text */
    }
  }

  p.tick-label {
    font-size: var(--theme-font-size-sm) !important;
    white-space: nowrap;

    &.y {
      transform: translate(-30px, -100%);
    }

    &.x {
      transform: translate(5px, 0px);
    }
  }

  p.end-val {
    transform: translate(0, -50%);
    font-size: var(--theme-font-size-sm) !important;
    font-weight: 400 !important;
  }

  .background.bar-chart-overlay {
    position: relative;
  }

  // .stacked-data-bar {
  //   mask: url(#mask-stripe);
  // }

  .new-projection.data-bar.year-2025,
  .new-projection.data-bar.year-2026,
  .new-projection.data-bar.year-2027 {
    fill: #006075 !important; // dark blue
  }
</style>
